import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronLeft, Droplet, Plus, Trash2, User } from 'lucide-react';
import { dayKeyIn } from '@shared/prayer';
import { alive, useProgress, useSettings } from '@/lib/store';
import type { PeriodEntry } from '@/lib/types';
import { IconButton, Row, Section, Segmented, Sheet, Switch } from '@/components/ui';
import { authClient, useSession } from '@/lib/auth';
import { periodLength } from '@/features/fasting/fastMetrics';

export function GenderPicker({ value, onChange }: { value: 'female' | 'male' | null; onChange: (v: 'female' | 'male' | null) => void }) {
  return (
    <Segmented label="I am" value={value ?? 'none'} onChange={(v) => onChange(v === 'none' ? null : v)}
      options={[{ value: 'female', label: 'A sister' }, { value: 'male', label: 'A brother' }, { value: 'none', label: 'Prefer not to say' }]} />
  );
}

const fmt = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** Edit your name — updates the account when signed in, otherwise just this device. */
export function NameSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: session } = useSession();
  const local = useSettings((s) => s.settings.displayName);
  const update = useSettings((s) => s.update);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (open) { setName(session?.user?.name ?? local); setErr(null); } }, [open, session?.user?.name, local]);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = name.trim().slice(0, 60);
    if (!v) { setErr('Please enter a name.'); return; }
    setBusy(true);
    if (session?.user) {
      const r = await authClient.updateUser({ name: v });
      if (r.error) { setBusy(false); setErr(r.error.message ?? 'Could not save — try again.'); return; }
    }
    update({ displayName: v });
    setBusy(false);
    onClose();
  };
  return (
    <Sheet open={open} onClose={onClose} title="Your name">
      <form onSubmit={save} className="space-y-3 px-5 pb-2">
        <input className="w-full rounded-xl bg-fill px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-accent" aria-label="Your name" autoComplete="name" autoFocus maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
        <p className="text-[13px] text-muted">{session?.user ? 'Saved to your account. Home greets you by your first name.' : 'Saved on this device. Home greets you by your first name.'}</p>
        {err && <p className="text-[14px] text-danger">{err}</p>}
        <button type="submit" disabled={busy} className="press w-full rounded-2xl bg-accent py-3.5 text-[17px] font-semibold text-accent-ink disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
      </form>
    </Sheet>
  );
}

export function ProfileSettings() {
  const nav = useNavigate();
  const { gender, syncPeriods, prayer } = useSettings((s) => s.settings);
  const update = useSettings((s) => s.update);
  const periods = alive(useProgress((s) => s.periods)).sort((a, b) => b.start.localeCompare(a.start));
  const deletePeriod = useProgress((s) => s.deletePeriod);
  const [editing, setEditing] = useState<Partial<PeriodEntry> | null>(null);
  const [editName, setEditName] = useState(false);
  const { data: session } = useSession();
  const displayName = useSettings((s) => s.settings.displayName);
  const today = dayKeyIn(prayer.tz);

  return (
    <div className="page-pad">
      <div className="flex items-center gap-2 px-3 pb-3">
        <IconButton label="Back" onClick={() => nav(-1)}><ChevronLeft size={20} /></IconButton>
        <h1 className="text-[28px] font-bold">Profile</h1>
      </div>
      <Section>
        <Row icon={<User size={18} />} label="Name" detail={session?.user?.name || displayName || 'Add your name'} onClick={() => setEditName(true)} />
      </Section>
      <NameSheet open={editName} onClose={() => setEditName(false)} />
      <div className="px-4 pb-2"><GenderPicker value={gender} onChange={(g) => update({ gender: g })} /></div>
      <p className="px-8 pb-6 text-[13px] text-muted">Sisters can mark their period (ḥayḍ) or post-natal bleeding (nifās). Prayers on those days are excused and won’t break streaks, and missed Ramadan fasts are added to the make-up count.</p>

      {gender === 'female' && (
        <>
          <Section title="Period log" footer="Start and end a period from the Prayer or Fasting screen, or add past ones here.">
            {periods.length === 0 && <Row label={<span className="text-muted">No periods logged yet</span>} />}
            {periods.map((p) => (
              <div key={p.id} className="row">
                <Droplet size={16} className="text-[#e87ba4]" />
                <button type="button" className="press min-w-0 flex-1 text-left" onClick={() => setEditing(p)}>
                  <span className="block font-medium">{fmt(p.start)} – {p.end ? fmt(p.end) : 'ongoing'}</span>
                  <span className="block text-[12px] text-muted">{p.kind === 'nifas' ? 'Nifās' : 'Ḥayḍ'} · {periodLength(p, today)} day{periodLength(p, today) === 1 ? '' : 's'}</span>
                </button>
                <button type="button" aria-label="Delete" className="press p-2 text-faint" onClick={() => { if (confirm('Delete this entry?')) deletePeriod(p.id); }}><Trash2 size={16} /></button>
              </div>
            ))}
            <Row icon={<Plus size={18} />} label="Add a past period" onClick={() => setEditing({ kind: 'hayd', start: today, end: today })} />
          </Section>
          <Section title="Privacy" footer="Your period log stays on this device unless you turn this on. It is never shown to anyone else.">
            <Row label="Back up period log to my account"><Switch label="Sync period log" checked={syncPeriods} onChange={(v) => update({ syncPeriods: v })} /></Row>
          </Section>
        </>
      )}
      <PeriodEditor value={editing} today={today} onClose={() => setEditing(null)} />
    </div>
  );
}

function PeriodEditor({ value, today, onClose }: { value: Partial<PeriodEntry> | null; today: string; onClose: () => void }) {
  const save = useProgress((s) => s.savePeriod);
  const [draft, setDraft] = useState<Partial<PeriodEntry> | null>(null);
  const v = draft ?? value;
  if (!value || !v) return null;
  const input = 'rounded-lg bg-fill px-2 py-1.5 text-ink';
  const close = () => { setDraft(null); onClose(); };
  return (
    <Sheet open onClose={close} title={value.id ? 'Edit period' : 'Add a period'}>
      <div className="px-4 pb-4">
        <Segmented label="Type" value={v.kind ?? 'hayd'} onChange={(k) => setDraft({ ...v, kind: k })}
          options={[{ value: 'hayd', label: 'Period (ḥayḍ)' }, { value: 'nifas', label: 'Post-natal (nifās)' }]} />
      </div>
      <Section>
        <Row label="Started"><input type="date" max={today} value={v.start} onChange={(e) => setDraft({ ...v, start: e.target.value })} className={input} aria-label="Start date" /></Row>
        <Row label="Ended"><input type="date" max={today} min={v.start} value={v.end ?? ''} onChange={(e) => setDraft({ ...v, end: e.target.value || undefined })} className={input} aria-label="End date" /></Row>
      </Section>
      <div className="px-4">
        <button type="button" disabled={!v.start} onClick={() => { save({ id: v.id, kind: v.kind ?? 'hayd', start: v.start!, end: v.end && v.end >= v.start! ? v.end : undefined }); close(); }}
          className="press w-full rounded-2xl bg-accent py-3.5 text-[17px] font-semibold text-accent-ink disabled:opacity-40">Save</button>
      </div>
    </Sheet>
  );
}
